/**
 * Lokaler Speicher der PWA (IndexedDB).
 *
 * Drei Ablagen:
 *
 *   boegen         heruntergeladene Prüfbögen, damit der Prüfer den Platz
 *                  ohne Verbindung durchgehen kann
 *   warteschlange  Änderungen, die noch zum Server müssen
 *   fotos          die Bilddaten zu den wartenden Fotoeinträgen
 *
 * Warum nicht localStorage: Fotos sind zu groß, und localStorage arbeitet
 * blockierend. Warum nicht der Zwischenspeicher des Service Workers: der
 * ist für Dateien gedacht, nicht für Fachdaten mit Reihenfolge.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { Pruefbogen } from '../api/typen'

/** Ein Eintrag in der Warteschlange. */
export type Auftrag = {
  id?: number
  /** Fachliche Art – bestimmt, wie ein Fehlschlag behandelt wird. */
  art: 'befund' | 'zuruecksetzen' | 'foto' | 'abschluss' | 'baustein'
  pruefung_id: string
  /** Der Befund, den dieser Auftrag betrifft. Für die Anzeige im Bogen. */
  befund_id?: string
  pfad: string
  methode: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  koerper?: unknown
  /** Verweis auf den Eintrag in der Ablage `fotos`. */
  foto_schluessel?: string
  erstellt_am: number
  versuche: number
  /** Gesetzt, wenn der Server den Auftrag endgültig abgelehnt hat. */
  fehler?: string
}

export type WartendesFoto = {
  schluessel: string
  pruefung_id: string
  befund_id?: string
  art: 'uebersicht' | 'detail'
  blob: Blob
  /** Für die Vorschau im Bogen, solange das Foto noch nicht hochgeladen ist. */
  breite: number
  hoehe: number
}

interface SpuerDB extends DBSchema {
  boegen: {
    key: string
    value: { id: string; bogen: Pruefbogen; geladen_am: number }
  }
  warteschlange: {
    key: number
    value: Auftrag
    indexes: { 'nach-pruefung': string; 'nach-zeit': number }
  }
  fotos: {
    key: string
    value: WartendesFoto
    indexes: { 'nach-pruefung': string }
  }
}

let verbindung: Promise<IDBPDatabase<SpuerDB>> | undefined

export function db(): Promise<IDBPDatabase<SpuerDB>> {
  verbindung ??= openDB<SpuerDB>('spuer', 1, {
    // Ohne diese Behandler kann das Öffnen unbegrenzt warten – etwa wenn ein
    // anderer Tab die Datenbank offen hält. Ein Aufruf, der nie zurückkommt,
    // ist schlimmer als einer, der scheitert: auf einen Fehler kann der
    // Aufrufer reagieren, auf Stille nicht.
    blocked() {
      console.warn('IndexedDB blockiert: ein anderer Tab hält die Datenbank offen')
    },
    blocking() {
      // Diese Verbindung steht einer neueren Fassung im Weg. Schließen, damit
      // der andere Tab weiterkommt.
      void verbindung?.then((datenbank) => datenbank.close())
      verbindung = undefined
    },
    terminated() {
      // Der Browser hat die Verbindung gekappt (Speicherdruck, iOS). Beim
      // nächsten Zugriff neu öffnen statt auf einer toten Verbindung zu warten.
      verbindung = undefined
    },
    upgrade(datenbank) {
      datenbank.createObjectStore('boegen', { keyPath: 'id' })

      const schlange = datenbank.createObjectStore('warteschlange', {
        keyPath: 'id',
        autoIncrement: true,
      })
      schlange.createIndex('nach-pruefung', 'pruefung_id')
      // Die Reihenfolge ist fachlich wichtig: erst der Befund, dann der
      // Abschluss. Der Schlüssel ist aufsteigend, der Zeitindex dient nur
      // der Anzeige.
      schlange.createIndex('nach-zeit', 'erstellt_am')

      const bilder = datenbank.createObjectStore('fotos', { keyPath: 'schluessel' })
      bilder.createIndex('nach-pruefung', 'pruefung_id')
    },
  })
  return verbindung
}

// ── Prüfbögen ────────────────────────────────────────────────────

export async function bogenAblegen(bogen: Pruefbogen): Promise<void> {
  const datenbank = await db()
  await datenbank.put('boegen', { id: bogen.id, bogen, geladen_am: Date.now() })
}

export async function bogenLesen(id: string): Promise<Pruefbogen | undefined> {
  const datenbank = await db()
  return (await datenbank.get('boegen', id))?.bogen
}

export async function abgelegteBoegen(): Promise<
  { id: string; bogen: Pruefbogen; geladen_am: number }[]
> {
  const datenbank = await db()
  return datenbank.getAll('boegen')
}

export async function bogenVerwerfen(id: string): Promise<void> {
  const datenbank = await db()
  await datenbank.delete('boegen', id)
}

// ── Warteschlange ────────────────────────────────────────────────

export async function einreihen(auftrag: Omit<Auftrag, 'id' | 'versuche'>): Promise<number> {
  const datenbank = await db()
  return datenbank.add('warteschlange', { ...auftrag, versuche: 0 })
}

export async function offeneAuftraege(): Promise<Auftrag[]> {
  const datenbank = await db()
  const alle = await datenbank.getAll('warteschlange')
  // Nach Schlüssel sortiert, also in der Reihenfolge des Entstehens.
  return alle.sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
}

export async function auftraegeZu(pruefungId: string): Promise<Auftrag[]> {
  const datenbank = await db()
  return datenbank.getAllFromIndex('warteschlange', 'nach-pruefung', pruefungId)
}

export async function auftragErledigt(id: number, fotoSchluessel?: string): Promise<void> {
  const datenbank = await db()
  const geschaeft = datenbank.transaction(['warteschlange', 'fotos'], 'readwrite')
  await geschaeft.objectStore('warteschlange').delete(id)
  if (fotoSchluessel) await geschaeft.objectStore('fotos').delete(fotoSchluessel)
  await geschaeft.done
}

export async function auftragAktualisieren(auftrag: Auftrag): Promise<void> {
  const datenbank = await db()
  await datenbank.put('warteschlange', auftrag)
}

// ── Wartende Fotos ───────────────────────────────────────────────

export async function fotoZwischenlegen(foto: WartendesFoto): Promise<void> {
  const datenbank = await db()
  await datenbank.put('fotos', foto)
}

export async function wartendesFoto(schluessel: string): Promise<WartendesFoto | undefined> {
  const datenbank = await db()
  return datenbank.get('fotos', schluessel)
}

export async function wartendeFotosZu(pruefungId: string): Promise<WartendesFoto[]> {
  const datenbank = await db()
  return datenbank.getAllFromIndex('fotos', 'nach-pruefung', pruefungId)
}

/**
 * Alles löschen – beim Abmelden.
 *
 * Ein geteiltes Diensthandy darf nach dem Abmelden keine Prüfdaten des
 * Vorgängers mehr enthalten.
 */
export async function allesVerwerfen(): Promise<void> {
  const datenbank = await db()
  const geschaeft = datenbank.transaction(['boegen', 'warteschlange', 'fotos'], 'readwrite')
  await Promise.all([
    geschaeft.objectStore('boegen').clear(),
    geschaeft.objectStore('warteschlange').clear(),
    geschaeft.objectStore('fotos').clear(),
  ])
  await geschaeft.done
}
